import { useEffect, useState } from "react";
import { formatUnits, parseUnits } from "ethers";
import { formatContractError } from "@/lib/error";
import { useContracts } from "@/hooks/useContracts";
import { useCatDetails } from "@/hooks/useCatDetails";
import { useCatclawAllowance } from "@/hooks/useCatclawAllowance";
import { ADDRESSES, getProvider } from "@/lib/contracts";
import { getTierBurn } from "@/lib/chainConstants";
import { Button } from "@/components/ui/button";
import { Loader2, Flame } from "lucide-react";
import { refreshAfterTx } from "@/lib/refreshAll";

const MAX_TIER = 4;

interface UpgradePanelProps {
  tokenId?: number | null;
  onDone?: () => void;
}

export function UpgradePanel({ tokenId, onDone }: UpgradePanelProps) {
  const { nft, catclaw, connected, requestConnect } = useContracts();
  const { details } = useCatDetails(tokenId);
  const { allowance, approve, refresh: refreshAllowance } =
    useCatclawAllowance(ADDRESSES.CatStonks);
  const [decimals, setDecimals] = useState<number>(18);
  const [nextCost, setNextCost] = useState<bigint | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    catclaw
      .decimals()
      .then((d: bigint) => setDecimals(Number(d)))
      .catch(() => setDecimals(18));
  }, [catclaw]);

  useEffect(() => {
    if (!tokenId || details?.tier === undefined) return;
    const current = details.tier;
    if (current >= MAX_TIER) {
      setNextCost(0n);
      return;
    }
    Promise.all([getTierBurn(current), getTierBurn(current + 1)])
      .then(([currentBurn, nextBurn]: [bigint, bigint]) => {
        setNextCost(nextBurn - currentBurn);
      })
      .catch(() => setNextCost(null));
  }, [tokenId, details?.tier]);

  if (!connected) {
    return (
      <Button variant="pixel" size="pixel" className="w-full" onClick={requestConnect}>
        Connect Wallet
      </Button>
    );
  }

  if (!tokenId) {
    return <p className="text-sm text-muted-foreground">Select a cat to upgrade.</p>;
  }

  if (!details?.active) {
    return <p className="text-sm text-muted-foreground">Activate this cat first.</p>;
  }

  if (details.tier >= MAX_TIER) {
    return <p className="text-sm text-success">Max tier reached.</p>;
  }

  const required = nextCost ?? parseUnits("1000000", decimals);
  const needsApprove = allowance < required;

  const handleUpgrade = async () => {
    if (!tokenId || details?.tier === undefined) return;
    setError(null);
    setIsPending(true);
    try {
      if (needsApprove) {
        setStep("Approve");
        await approve();
        await refreshAllowance();
      }

      setStep("Upgrade");
      const nextTier = details.tier + 1;
      const provider = getProvider();
      const tx = await nft.upgradeTier(tokenId, nextTier);
      await provider.waitForTransaction(tx.hash, 1, 30000);
      await refreshAllowance();
      await refreshAfterTx(tokenId);
      onDone?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
      setStep(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="font-pixel text-xs text-primary">CURRENT TIER</p>
        <p className="mt-1 font-mono text-sm">Tier {details.tier}</p>
      </div>

      {nextCost !== null && nextCost > 0n && (
        <div className="rounded-xl border border-border bg-background p-4">
          <p className="font-pixel text-xs text-primary">NEXT UPGRADE COST</p>
          <p className="mt-1 font-mono text-sm">{formatUnits(nextCost, decimals)} $CATCLAW</p>
        </div>
      )}

      <Button
        variant="pixel"
        size="pixel"
        className="w-full"
        onClick={handleUpgrade}
        disabled={isPending}
      >
        {isPending ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {step ?? "Upgrading"}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Flame size={16} />
            Upgrade to Tier {details.tier + 1}
          </span>
        )}
      </Button>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
